/**
 * LotMgtFormDialog — bám Access spec `frmFactoryQryLotMgtAdd`
 * (`plan/access_all/form_frmFactoryQryLotMgtAdd.txt`). Structure mirror
 * `IssueFormDialog` (same New/Edit dialog pattern), fields theo Lot Mgt spec.
 *
 * Layout: giữ nguyên thứ tự field (index order) theo Access TabIndex, FE đổ vào
 * GRID 3-COL column-major (12 field/cột, MAIN_FIELDS render theo thứ tự → DOM
 * order = Tab order). Contract tách xuống HÀNG RIÊNG dưới cùng (4-col).
 *   Col 1 (R1-12): lot_id(*), lot_name, lot_area(*), no_of_bag, quantity_mt,
 *                  cuptest, mc_rate, blacks_rate┐, brokens_rate┘→black_and_broken_rate,
 *                  black_and_broken_rate(auto), fm_rate, mouldy_rate
 *   Col 2 (R1-12): brown_rate, partly_black_rate, immature_rate,
 *                  broken_over_1_per_2_rate, insect_beans_rate, triages_defects_rate,
 *                  fb_rate, overdry_rate, cherry_rate, yellowbeans_rate,
 *                  polish_degree_rate, s20_rate
 *   Col 3 (R1-8):  s19_rate, s18_rate, s16_rate, s15_rate, s14_rate, s13_rate,
 *                  s12_rate, under_s12_rate
 *   Bottom row:    contract_no (combo, load suggest qua API GET lot-mgt-contracts),
 *                  contract_quality / contract_client / contract_package (readonly
 *                  DLookUp public.mv_local_s — auto-fill khi chọn contract_no).
 *
 * Hidden (`Visible = NotDefault`): no_of_cont, shipped_cont, actual_shipped_mt,
 *   id, id2 → KHÔNG render (BE giữ giá trị cũ / default).
 * Audit footer (`Enabled = NotDefault`, Top=7680): created/updated_by/_date,
 *   is_status → BE tự fill.
 * Required (Form_Load): `strCheck = ",lot_id,lot_area,"`.
 * Cascade (blacks_rate / brokens_rate AfterUpdate):
 *   black_and_broken_rate = Nz(blacks_rate,0) + Nz(brokens_rate,0).
 * contract_quality / contract_client / contract_package: Access DLookUp display
 *   từ public_mv_local_s theo contract_no (KHÔNG nằm trong strU/strI → không
 *   lưu). FE render readonly, hydrate best-effort từ row khi Edit; không có
 *   lookup endpoint nên New mode để trống.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  useGetLotMgtContractsQuery,
  useGetReferenceCombosQuery,
  useLazyCheckLotIdExistsQuery,
  useLazyGetSuggestLotIdQuery,
  type FactoryRowPayload,
  type FactoryCell,
} from '@/store/api/factoryApi'
import { FORM_GRID_3COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type LotMgtRow = { id: string | number } & Record<string, FactoryCell>

/** Editable numeric fields (Access Format Standard `#,##0.00`). */
const NUMERIC_KEYS = [
  'no_of_bag',
  'quantity_mt',
  'mc_rate',
  'blacks_rate',
  'brokens_rate',
  'black_and_broken_rate',
  'fm_rate',
  'mouldy_rate',
  'brown_rate',
  'partly_black_rate',
  'immature_rate',
  'broken_over_1_per_2_rate',
  'insect_beans_rate',
  'triages_defects_rate',
  'fb_rate',
  'overdry_rate',
  'cherry_rate',
  'yellowbeans_rate',
  'polish_degree_rate',
  's20_rate',
  's19_rate',
  's18_rate',
  's16_rate',
  's15_rate',
  's14_rate',
  's13_rate',
  's12_rate',
  'under_s12_rate',
] as const

type NumericKey = (typeof NUMERIC_KEYS)[number]

interface DraftState {
  lot_id: string
  lot_name: string
  lot_area: string
  cuptest: string
  contract_no: string
  no_of_bag: string
  quantity_mt: string
  mc_rate: string
  blacks_rate: string
  brokens_rate: string
  black_and_broken_rate: string
  fm_rate: string
  mouldy_rate: string
  brown_rate: string
  partly_black_rate: string
  immature_rate: string
  broken_over_1_per_2_rate: string
  insect_beans_rate: string
  triages_defects_rate: string
  fb_rate: string
  overdry_rate: string
  cherry_rate: string
  yellowbeans_rate: string
  polish_degree_rate: string
  s20_rate: string
  s19_rate: string
  s18_rate: string
  s16_rate: string
  s15_rate: string
  s14_rate: string
  s13_rate: string
  s12_rate: string
  under_s12_rate: string
}

const EMPTY_DRAFT: DraftState = {
  lot_id: '',
  lot_name: '',
  lot_area: '',
  cuptest: '',
  contract_no: '',
  no_of_bag: '',
  quantity_mt: '',
  mc_rate: '',
  blacks_rate: '',
  brokens_rate: '',
  black_and_broken_rate: '',
  fm_rate: '',
  mouldy_rate: '',
  brown_rate: '',
  partly_black_rate: '',
  immature_rate: '',
  broken_over_1_per_2_rate: '',
  insect_beans_rate: '',
  triages_defects_rate: '',
  fb_rate: '',
  overdry_rate: '',
  cherry_rate: '',
  yellowbeans_rate: '',
  polish_degree_rate: '',
  s20_rate: '',
  s19_rate: '',
  s18_rate: '',
  s16_rate: '',
  s15_rate: '',
  s14_rate: '',
  s13_rate: '',
  s12_rate: '',
  under_s12_rate: '',
}

interface MainField {
  key: keyof DraftState
  label: string
  kind: 'text' | 'combo' | 'numeric'
  required?: boolean
  pk?: boolean // lot_id — PK, disabled khi edit
  cascade?: boolean // blacks_rate/brokens_rate → black_and_broken_rate
}

/** TOP — thông tin Lot (định danh + số lượng) + Contract. Render row-major
 *  (auto-flow) → DOM order = thứ tự đọc = Tab order. Contract block render riêng
 *  ngay sau (cùng grid). */
const TOP_FIELDS: MainField[] = [
  { key: 'lot_id', label: 'Lot ID', kind: 'text', required: true, pk: true },
  { key: 'lot_name', label: 'Lot Name', kind: 'text' },
  { key: 'lot_area', label: 'Lot Area', kind: 'combo', required: true },
  { key: 'no_of_bag', label: 'No Of Bag', kind: 'numeric' },
  { key: 'quantity_mt', label: 'Quantity KG', kind: 'numeric' },
  { key: 'cuptest', label: 'Cuptest', kind: 'text' },
]

/** BOTTOM — deductions (.r) + screen sizes (S..). Column-major 3-col: DOM order
 *  == Tab order (Tab top→bottom đúng từng cột). */
const RATE_FIELDS: MainField[] = [
  // ── Deductions ──
  { key: 'mc_rate', label: 'MC.r', kind: 'numeric' },
  { key: 'blacks_rate', label: 'Blacks.r', kind: 'numeric', cascade: true },
  { key: 'brokens_rate', label: 'Brokens.r', kind: 'numeric', cascade: true },
  { key: 'black_and_broken_rate', label: 'Black And Broken.r', kind: 'numeric' },
  { key: 'fm_rate', label: 'FM.r', kind: 'numeric' },
  { key: 'mouldy_rate', label: 'Mouldy.r', kind: 'numeric' },
  { key: 'brown_rate', label: 'Brown.r', kind: 'numeric' },
  { key: 'partly_black_rate', label: 'Partly Black.r', kind: 'numeric' },
  { key: 'immature_rate', label: 'Immature.r', kind: 'numeric' },
  { key: 'broken_over_1_per_2_rate', label: 'Broken > 1/2.r', kind: 'numeric' },
  { key: 'insect_beans_rate', label: 'Insect.r', kind: 'numeric' },
  { key: 'triages_defects_rate', label: 'Triages Defects.r', kind: 'numeric' },
  { key: 'fb_rate', label: 'FB.r', kind: 'numeric' },
  { key: 'overdry_rate', label: 'Overdry.r', kind: 'numeric' },
  { key: 'cherry_rate', label: 'Cherry.r', kind: 'numeric' },
  { key: 'yellowbeans_rate', label: 'Yellowbeans.r', kind: 'numeric' },
  { key: 'polish_degree_rate', label: 'Polish Degree.r', kind: 'numeric' },
  // ── Screen ──
  { key: 's20_rate', label: 'S20.r', kind: 'numeric' },
  { key: 's19_rate', label: 'S19.r', kind: 'numeric' },
  { key: 's18_rate', label: 'S18.r', kind: 'numeric' },
  { key: 's16_rate', label: 'S16.r', kind: 'numeric' },
  { key: 's15_rate', label: 'S15.r', kind: 'numeric' },
  { key: 's14_rate', label: 'S14.r', kind: 'numeric' },
  { key: 's13_rate', label: 'S13.r', kind: 'numeric' },
  { key: 's12_rate', label: 'S12.r', kind: 'numeric' },
  { key: 'under_s12_rate', label: '< S12.r', kind: 'numeric' },
]

const RATE_ROWS_PER_COL = Math.ceil(RATE_FIELDS.length / 3)

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Bg cho field `Enabled = NotDefault` (contract_* readonly + lot_id khi Edit).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
}

// Access `Format = "Standard"` = `#,##0.00` (thousand sep + 2 decimals).
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

// Parse user input ("1,234.5" / "1234.5" / "") → number | null.
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Format raw value → Standard string (`1,234.56` / `''`).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface LotMgtFormDialogProps {
  open: boolean
  /** 'new' = draft rỗng; 'edit' = hydrate row, lot_id khoá (PK); 'copy' =
   *  hydrate row (clone mọi field) NHƯNG lot_id reset rỗng + editable để user
   *  nhập mã mới (bám Access `btnCopy_Click`: clone RecordSource + lot_id="" +
   *  id=0 → Save = INSERT mới). */
  mode: 'new' | 'edit' | 'copy'
  row: LotMgtRow | null
  saving: boolean
  factoryCode: string
  /** Permission gating — false → ẩn Save + show read-only banner (layer 4). */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

export function LotMgtFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  canEdit = true,
  onClose,
  onSave,
}: LotMgtFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  // ── Suggest-No flow (skill access-suggest) — bám Access `btnNew_Click` /
  //    `btnCopy_Click` (`lot_id = Suggest_IndexName(... & "lot_mgt")`) +
  //    `lot_id_AfterUpdate` duplicate check. New/Copy auto-fill mã + onBlur
  //    re-check → regen khi trùng (vd 2 form New mở cùng lúc → save form 1 →
  //    blur form 2 tự đổi mã). Edit: KHÔNG suggest/check (PK không đổi). ──
  const [triggerSuggest, suggestState] = useLazyGetSuggestLotIdQuery()
  const [triggerCheckLot] = useLazyCheckLotIdExistsQuery()
  // Cảnh báo inline khi pre-check phát hiện lot_id đã có row khác dùng → tự
  // suggest mã mới + alert (chỉ thông báo, KHÔNG snack, KHÔNG auto-save).
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  // Loading flag cho pre-check để disable Save khi đang verify.
  const [precheckBusy, setPrecheckBusy] = useState(false)
  // Synchronous mirror — setState async, handleSave đọc giá trị cũ trong cùng
  // tick (blur field PK + click Save) → ref cho phép guard race sync.
  const precheckBusyRef = useRef(false)

  // Reset suggest-flow state mỗi lần mở dialog.
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode, row])

  // Auto-suggest lot_id cho New/Copy (force network — keepUnusedDataFor:0).
  useEffect(() => {
    if (!open || mode === 'edit') return
    void triggerSuggest({ factoryCode })
  }, [open, mode, factoryCode, triggerSuggest])

  // Áp suggestion khi arrive + field còn rỗng (user/Copy có thể đã có giá trị).
  useEffect(() => {
    const suggested = suggestState.data?.lot_id
    if (!suggested || mode === 'edit') return
    setDraft((prev) => (prev.lot_id.trim().length ? prev : { ...prev, lot_id: suggested }))
  }, [suggestState.data, mode])

  // Dismiss alert khi user sửa lot_id khác mã suggest.
  useEffect(() => {
    if (regenNotice && draft.lot_id !== regenNotice.to) setRegenNotice(null)
  }, [draft.lot_id, regenNotice])

  // Hydrate draft khi mở. New → empty. Edit/Copy → row values (numeric
  // pre-format Standard); Copy reset lot_id rỗng để user nhập mã mới (Access
  // btnCopy_Click). Chỉ depend [open, mode, row] (memory feedback_hydrate_effect_deps).
  useEffect(() => {
    if (!open) return
    if ((mode === 'edit' || mode === 'copy') && row) {
      const next: DraftState = { ...EMPTY_DRAFT }
      next.lot_id = mode === 'copy' ? '' : String(row.lot_id ?? '')
      next.lot_name = String(row.lot_name ?? '')
      next.lot_area = String(row.lot_area ?? '')
      next.cuptest = String(row.cuptest ?? '')
      next.contract_no = String(row.contract_no ?? '')
      for (const k of NUMERIC_KEYS) {
        next[k] = formatStandard(row[k] as number | string | null)
      }
      setDraft(next)
    } else {
      setDraft(EMPTY_DRAFT)
    }
  }, [open, mode, row])

  // lot_area combo = Access RowSource `public_factory_area` (master, scope
  // factory) — KHÔNG distinct từ list. Strict (LimitToList=True), value lưu =
  // full_area_code, label "<full_area_code> — <stock>" (đồng bộ StackFormDialog).
  const { data: combos } = useGetReferenceCombosQuery({ factoryCode }, { skip: !open })
  const lotAreaOptions = useMemo<{ value: string; label: string }[]>(() => {
    const list = (combos?.areas ?? []).map((o) => {
      const code = o.full_area_code
      const stock = (o.stock ?? '').trim()
      return { value: code, label: stock ? `${code} — ${stock}` : code }
    })
    const cur = draft.lot_area.trim()
    if (!cur || list.some((o) => o.value === cur)) return list
    return [{ value: cur, label: cur }, ...list]
  }, [combos, draft.lot_area])

  // Contract suggest — Access contract_no combo + DLookUp (public.mv_local_s,
  // finalized_text='N'). Call API khi dialog mở; chọn contract_no → auto-fill
  // quality/client/package (readonly, không lưu — chỉ contract_no nằm trong strU/strI).
  // Edit: gửi kèm contract_no đang lưu → BE append nó vào list (kể cả đã
  // finalized 'Y') để 3 field readonly quality/client/package auto-fill được.
  const hydratedContractNo =
    (mode === 'edit' || mode === 'copy') && row ? String(row.contract_no ?? '').trim() : ''
  const contractsQuery = useGetLotMgtContractsQuery(
    { factoryCode, contractNo: hydratedContractNo || undefined },
    { skip: !open },
  )
  const contracts = useMemo(() => contractsQuery.data ?? [], [contractsQuery.data])
  const contractNoOptions = useMemo(() => contracts.map((c) => c.contract_no), [contracts])
  // Combobox options: gồm cả contract_no đang lưu (Edit) nếu nó không còn trong
  // list finalized_text='N' → tránh warning "value not in options" + giữ value cũ.
  const contractNoSelectOptions = useMemo(() => {
    const cur = draft.contract_no.trim()
    if (cur && !contractNoOptions.includes(cur)) return [cur, ...contractNoOptions]
    return contractNoOptions
  }, [contractNoOptions, draft.contract_no])
  const contractByNo = useMemo(() => {
    const m = new Map<string, (typeof contracts)[number]>()
    for (const c of contracts) m.set(c.contract_no, c)
    return m
  }, [contracts])
  // Display readonly: ưu tiên lookup theo contract_no đang chọn; fallback row (Edit).
  const picked = contractByNo.get(draft.contract_no.trim())
  const hasRow = (mode === 'edit' || mode === 'copy') && row
  const contractQuality = picked
    ? picked.contract_quality ?? ''
    : hasRow
      ? String(row.contract_quality ?? '')
      : ''
  const contractClient = picked
    ? picked.contract_client ?? ''
    : hasRow
      ? String(row.contract_client ?? '')
      : ''
  const contractPackage = picked
    ? picked.contract_package ?? ''
    : hasRow
      ? String(row.contract_package ?? '')
      : ''

  const lotIdEmpty = draft.lot_id.trim().length === 0
  const lotAreaEmpty = draft.lot_area.trim().length === 0
  const canSave = canEdit && !lotIdEmpty && !lotAreaEmpty && !saving
  const modeLabel = mode === 'edit' ? 'Edit' : mode === 'copy' ? 'Copy' : 'New'

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Auto-format Standard khi blur khỏi field số.
  const handleNumericBlur = (key: NumericKey) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      return formatted !== prev[key] ? { ...prev, [key]: formatted } : prev
    })
  }

  // Access blacks_rate/brokens_rate AfterUpdate: black_and_broken_rate = sum.
  // Recompute trên blur (= thời điểm AfterUpdate commit) + format cả 2 field.
  const handleCascadeBlur = (key: 'blacks_rate' | 'brokens_rate') => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      const blacks = key === 'blacks_rate' ? formatted : prev.blacks_rate
      const brokens = key === 'brokens_rate' ? formatted : prev.brokens_rate
      const sum = (parseNumeric(blacks) ?? 0) + (parseNumeric(brokens) ?? 0)
      return {
        ...prev,
        [key]: formatted,
        black_and_broken_rate: formatStandard(sum),
      }
    })
  }

  // lot_id_AfterUpdate parity — onBlur field PK (New/Copy): nếu lot_id đã tồn
  // tại → force-suggest mã mới + alert. Edit không check (PK không đổi).
  const handleLotIdBlur = async () => {
    if (mode === 'edit') return
    const lot = draft.lot_id.trim()
    if (!lot) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckLot({ factoryCode, lotId: lot }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network: lần suggest đầu khi mở form đã
      // subscribe cache; nếu không force, RTK trả mã cũ (đang trùng) → skip.
      const next = await triggerSuggest({ factoryCode }, false).unwrap()
      const newVal = (next.lot_id || '').trim()
      if (newVal && newVal !== lot) {
        setDraft((prev) => ({ ...prev, lot_id: newVal }))
        setRegenNotice({ from: lot, to: newVal })
      }
    } catch {
      // BE raise lúc Save nếu thật sự trùng — không poison UX ở blur.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  // Map btnSave_Click: isValid(Me,strCheck) → Update_Insert(strU,strI,Me).
  // Build payload theo strI (new) — handleSaveEdit (parent) tự inject PK lot_id.
  const handleSave = () => {
    if (precheckBusyRef.current) return // RACE GUARD: blur pre-check đang chạy
    if (!canSave) return
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const payload: FactoryRowPayload = {
      lot_id: draft.lot_id.trim(),
      lot_name: str(draft.lot_name),
      lot_area: draft.lot_area.trim(),
      cuptest: str(draft.cuptest),
    }
    for (const k of NUMERIC_KEYS) {
      payload[k] = parseNumeric(draft[k])
    }
    // contract_no: chỉ gửi khi có giá trị. List view (`view_factory_lot_mgt1`)
    // có thể không expose contract_no → Edit hydrate trống; gửi null sẽ xoá
    // giá trị đang lưu. Omit khi trống = giữ nguyên (BE update bỏ qua cột thiếu).
    const cn = draft.contract_no.trim()
    if (cn) payload.contract_no = cn
    onSave(payload)
  }

  // Map btnCancel_Click — Undo (draft unmount) + Close. Block khi saving.
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // Render 1 field (text/combo/numeric) — dùng chung cho grid Top + Bottom.
  // `sx` = grid placement (Bottom truyền gridColumn/gridRow; Top auto-flow → bỏ).
  const renderField = (f: MainField, sx?: object) => {
    if (f.kind === 'combo') {
      // lot_area — strict (Access LimitToList=True), KHÔNG freeSolo.
      return (
        <Autocomplete
          key={f.key}
          sx={sx}
          size="small"
          options={lotAreaOptions}
          value={lotAreaOptions.find((o) => o.value === draft[f.key]) ?? null}
          onChange={(_, v) => handleChange(f.key, v?.value ?? '')}
          getOptionLabel={(o) => (o ? o.label : '')}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => (
            <TextField {...params} label={f.label} required={f.required} fullWidth />
          )}
        />
      )
    }
    if (f.kind === 'numeric') {
      const key = f.key as NumericKey
      const onBlur = f.cascade
        ? handleCascadeBlur(f.key as 'blacks_rate' | 'brokens_rate')
        : handleNumericBlur(key)
      return (
        <TextField
          key={f.key}
          sx={sx}
          label={f.label}
          size="small"
          fullWidth
          type="text"
          value={draft[key]}
          onChange={(e) => handleChange(key, e.target.value)}
          onBlur={onBlur}
          inputProps={numericInputProps}
        />
      )
    }
    // text — lot_id (PK lock khi edit + suggest/check khi New/Copy), lot_name, cuptest
    const lockedPk = !!f.pk && mode === 'edit'
    const isPk = !!f.pk
    const pkHelper = !isPk
      ? undefined
      : mode === 'edit'
        ? 'Cannot be changed after creation.'
        : precheckBusy
          ? 'Verifying lot code…'
          : suggestState.isFetching
            ? 'Suggesting next lot code…'
            : mode === 'copy'
              ? `Suggested code below; cloned from ${String(row?.lot_id ?? '')}.`
              : 'Suggested code below; edit if needed.'
    return (
      <TextField
        key={f.key}
        sx={{ ...sx, ...(lockedPk ? readonlyFieldSx : {}) }}
        label={f.label}
        size="small"
        fullWidth
        required={f.required}
        disabled={lockedPk}
        autoFocus={!!f.pk && mode !== 'edit'}
        value={draft[f.key]}
        onChange={(e) => handleChange(f.key, e.target.value)}
        onBlur={isPk ? () => void handleLotIdBlur() : undefined}
        helperText={pkHelper}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(760px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          noWrap
          title={`${modeLabel} — Factory · Lot Mgt`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {modeLabel} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Lot Mgt'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave || precheckBusy}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}
      >
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Read-only — you don't have edit permission for this factory.
          </Alert>
        ) : null}
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Lot ID <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        {/* ── TOP — Lot info (định danh + No of bag + Quantity + Cuptest) +
           Contract. Grid 4-col row-major (auto-flow) → DOM = đọc = Tab order. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {TOP_FIELDS.map((f) =>
            renderField(f, f.key === 'lot_area' ? { gridColumn: { sm: 'span 2' } } : undefined),
          )}
        </Box>

        {/* Contract — hàng RIÊNG: contract_no combo load suggest qua API; chọn →
           auto-fill quality/client/package (readonly DLookUp). */}
        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          <Autocomplete<string, false, false, false>
            size="small"
            loading={contractsQuery.isFetching}
            options={contractNoSelectOptions}
            value={draft.contract_no || null}
            onChange={(_, v) => handleChange('contract_no', v ?? '')}
            renderOption={(props, option) => {
              const c = contractByNo.get(option)
              return (
                <li {...props} key={option}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option}
                    </Typography>
                    {c?.contract_client ? (
                      <Typography variant="caption" color="text.secondary">
                        {c.contract_client}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contract No"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {contractsQuery.isFetching ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Quality"
            size="small"
            fullWidth
            value={contractQuality}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Client"
            size="small"
            fullWidth
            value={contractClient}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={readonlyFieldSx}
            label="Contract Packaging"
            size="small"
            fullWidth
            value={contractPackage}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* ── BOTTOM — Deductions (.r) + Screen sizes. Column-major 3-col →
           DOM = Tab order (Tab top→bottom đúng từng cột). */}
        <Divider sx={{ my: 1.5 }} />
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.75, letterSpacing: '0.08em', lineHeight: 1.2 }}
        >
          Deductions & Screen
        </Typography>
        <Box sx={FORM_GRID_3COL}>
          {RATE_FIELDS.map((f, i) =>
            renderField(f, {
              gridColumn: { xs: 1, sm: Math.floor(i / RATE_ROWS_PER_COL) + 1 },
              gridRow: { sm: (i % RATE_ROWS_PER_COL) + 1 },
            }),
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default LotMgtFormDialog
